import { useCallback, useEffect, useMemo, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { api, formatApiErrorDetail } from "@/lib/api";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Plus, Search, PackageCheck, Truck } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["BASECOAT_TINTER", "PRIMER", "CLEARCOAT", "HARDENER", "THINNER"];

function AddPaint({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", paint_type: "BASECOAT_TINTER", paint_code: "", batch: "", unit: "ml", min_stock: 0 });
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Enter paint name"); return; }
    try { await api.post("/paints", { ...f, min_stock: Number(f.min_stock) || 0 }); toast.success("Paint added"); setOpen(false); setF({ name: "", paint_type: "BASECOAT_TINTER", paint_code: "", batch: "", unit: "ml", min_stock: 0 }); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="paint-add-btn" size="sm" className="bg-[#3B82F6] font-semibold text-gray-900 hover:bg-[#C5A059]"><Plus size={14} className="mr-1" /> Add Item</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">Add Paint</DialogTitle><DialogDescription>Create a paint stock item with code &amp; batch.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Name *</Label><Input data-testid="paint-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="text-xs">Type</Label>
            <Select value={f.paint_type} onValueChange={(v) => setF({ ...f, paint_type: v })}><SelectTrigger data-testid="paint-type" className="mt-1.5 h-9"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{label(t)}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Paint Code</Label><Input data-testid="paint-code" value={f.paint_code} onChange={(e) => setF({ ...f, paint_code: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs">Batch</Label><Input data-testid="paint-batch" value={f.batch} onChange={(e) => setF({ ...f, batch: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs">Unit</Label><Input data-testid="paint-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs">Min Stock</Label><Input data-testid="paint-min" type="number" value={f.min_stock} onChange={(e) => setF({ ...f, min_stock: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="flex justify-end"><Button data-testid="paint-add-confirm" onClick={submit} className="bg-[#111827] text-white hover:bg-[#1F2937]">Add</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PaintTxn({ item, kind, onDone }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [plate, setPlate] = useState("");
  const [notes, setNotes] = useState("");
  const isIssue = kind === "issue";
  const submit = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.error("Enter quantity"); return; }
    if (isIssue && n > item.qty_on_hand) { toast.error(`Only ${item.qty_on_hand} in stock`); return; }
    try {
      await api.post(`/paints/${item.id}/${kind}`, { qty: n, license_plate: plate, notes });
      toast.success(isIssue ? "Issued to repair order" : "Stock received");
      setOpen(false); setQty(""); setPlate(""); setNotes(""); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`paint-${kind}-open-${item.id}`} size="sm" variant={isIssue ? "default" : "outline"} className={isIssue ? "bg-[#111827] text-white hover:bg-[#1F2937]" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}>
          {isIssue ? <><Truck size={14} className="mr-1" /> Use</> : <><PackageCheck size={14} className="mr-1" /> Receive</>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading">{isIssue ? "Use / Issue Paint" : "Receive Paint"}</DialogTitle><DialogDescription>{item.name} {item.paint_code ? `· ${item.paint_code}` : ""} · on hand {item.qty_on_hand} {item.unit}</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Quantity *</Label><Input data-testid={`paint-${kind}-qty-${item.id}`} type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" /></div>
          {isIssue && <div><Label className="text-xs">License Plate / Repair Order (optional)</Label><Input data-testid={`paint-issue-plate-${item.id}`} value={plate} onChange={(e) => setPlate(e.target.value)} className="mt-1.5" placeholder="e.g. H 1988 BAG" /></div>}
          <div><Label className="text-xs">Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" /></div>
          <div className="flex justify-end"><Button data-testid={`paint-${kind}-confirm-${item.id}`} onClick={submit} className={isIssue ? "bg-[#111827] text-white" : "bg-emerald-600 text-white"}>Confirm</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WarehousePaint() {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("ALL");
  const [q, setQ] = useUrlState("q", "");
  const load = useCallback(async () => { try { const { data } = await api.get("/paints"); setItems(data || []); } catch (e) { /* ignore */ } }, []);
  useEffect(() => { load(); }, [load]);
  const visible = useMemo(() => items.filter((p) => (type === "ALL" || p.paint_type === type) && (!q || p.name.toLowerCase().includes(q.toLowerCase()) || (p.paint_code || "").toLowerCase().includes(q.toLowerCase()))), [items, type, q]);

  return (
    <div className="ti-dark -m-3 -mb-24 min-h-[calc(100vh-3rem)] px-4 py-4 sm:-m-5 sm:px-6 lg:-mb-5 lg:px-8 lg:py-6">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#111827] sm:text-2xl"><Palette size={26} className="mr-2 inline text-[#3B82F6]" />Paint Warehouse</h1>
          <p className="mt-1 text-sm text-gray-500">Paint stock · code, batch, received &amp; used · usage links to Repair Order</p>
        </div>
        <AddPaint onDone={load} />
      </div>
      <div className="relative mt-3">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input data-testid="paint-search" placeholder="Search paint or code…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-10" />
      </div>
      <div className="-mx-1 mt-3 flex gap-1.5 overflow-x-auto px-1 pb-1">
        {["ALL", ...TYPES].map((t) => (
          <button key={t} data-testid={`paint-type-${t}`} onClick={() => setType(t)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${type === t ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"}`}>{label(t)}</button>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visible.map((p) => {
          const low = Number(p.qty_on_hand) <= Number(p.min_stock || 0);
          return (
            <div key={p.id} data-testid={`paint-${p.id}`} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">{p.name}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">{label(p.paint_type)}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
                {p.paint_code && <span>Code {p.paint_code}</span>}
                {p.batch && <span>· Batch {p.batch}</span>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                <span className={`rounded-full px-2 py-0.5 font-semibold ${low ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>On hand {p.qty_on_hand} {p.unit}</span>
                {low && <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">LOW</span>}
                <span className="text-gray-400">Recv {p.qty_received} · Used {p.qty_used}</span>
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <PaintTxn item={p} kind="receive" onDone={load} />
                <PaintTxn item={p} kind="issue" onDone={load} />
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <p className="py-10 text-center text-sm text-gray-400 sm:col-span-2">No paint items yet. Use "Add Item" to create stock.</p>}
      </div>
    </div>
  );
}
